import type { JSX } from "react";
import { useI18n } from "../i18n/I18nContext";
import { cx } from "./classNames";
import styles from "./BrandHeader.module.css";

/**
 * Path of the product mark without text (ADR-0107), generated from `brand/zeitavo-mark.png` by
 * `scripts/brand/generate-icons.sh`.
 */
export const brandMarkPath = "/icons/mark.png";

/** Properties of {@link BrandHeader}. */
export interface BrandHeaderProps {
  /**
   * `bar`: the compact line in the top bar (the default). `hero`: the large centred block above the
   * sign-in button, on the navy ground of the sign-in screen (F-44, ADR-0160).
   */
  readonly variant?: "bar" | "hero" | undefined;
}

/**
 * Product mark, name and slogan above the sign-in (ADR-0107). The mark is decorative, because the
 * product name follows as text.
 *
 * @param props - Component properties.
 * @returns The brand element.
 */
export function BrandHeader(props: BrandHeaderProps): JSX.Element {
  const { t } = useI18n();
  const hero = props.variant === "hero";
  const markSize = hero ? "96" : "48";

  const brand = (
    <div className={cx(styles["brand"], hero ? styles["heroBrand"] : undefined)}>
      <img
        className={cx(styles["mark"], hero ? styles["heroMark"] : undefined)}
        src={brandMarkPath}
        alt=""
        width={markSize}
        height={markSize}
      />
      <div className={hero ? styles["heroText"] : undefined}>
        <p className={cx(styles["name"], hero ? styles["heroName"] : undefined)}>
          {t("app.title")}
        </p>
        <p className={cx(styles["slogan"], hero ? styles["heroSlogan"] : undefined)}>
          {t("app.slogan")}
        </p>
      </div>
    </div>
  );

  return brand;
}
